import { useMemo, useState } from "react";
import { useT } from "../i18n";
import { Blattvonunten } from "./Blattvonunten";
import { alsTag, kurzDatum, kwVon, montagVon } from "../daten/kalender";

/* ══════════════════════════════════════════════════════════════════
   Die Kalenderwoche — die grobe Vorplanung.

   Luca, 21.09.2026: *„Was bei der PV-Reinigung fehlt, ist eine
   Einordnung vom Datum, weil man ja auch abschätzen muss, wann das
   passiert. Man macht die grobe Vorplanung mit KW im Angebot, braucht
   kein festes Datum, und dann bei der Ortsaufnahme sagt man: Das kann
   ein genaues Datum sein."*

   **Warum kein Zeitraumwähler.** Der fragt nach zwei Tagen, und genau
   die gibt es hier noch nicht. Wer im Angebot ein Datum eintragen muss,
   trägt eines ein — und dann steht ein Tag im Brett, den niemand zugesagt
   hat, und der Kunde liest ihn auf dem Papier.

   **Warum kein `<input type="week">`.** Er ist in Safari und Firefox ein
   Textfeld. Ein Feld, in das man „KW 39" tippen kann und das im nächsten
   Browser einen Wähler zeigt, ist zwei verschiedene Masken mit einem
   Namen.

   **Eine Liste von Wochen, wie sie im Kopf gedacht werden:** „diese
   Woche", „nächste", dann Datum. Wer weiter hinaus muss, rollt — ein
   Kalender für eine Woche wäre ein Raster für eine Zeile.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Wie weit der Wähler nach vorn reicht — ein halbes Jahr.
 *
 * Wer weiter plant, plant keine Reinigung, sondern einen Turnus; dafür
 * steht `recurrence` im Datenmodell und nicht dieses Feld.
 */
const WOCHEN_VORAUS = 26;

/** Der Montag, `n` Wochen nach dem Montag dieser Woche. */
function montagPlus(n: number): Date {
  const mo = montagVon(new Date());
  mo.setDate(mo.getDate() + n * 7);
  return mo;
}

/** Der Sonntag zu einem Montag — der **letzte** Tag der Woche. */
export function sonntagZu(montag: Date): Date {
  const so = new Date(montag);
  so.setDate(so.getDate() + 6);
  return so;
}

/**
 * „KW 39 · 21.09.–27.09." — wie eine Woche im Text heißt.
 *
 * Beides zusammen, nie nur die Nummer: Kaum jemand weiß auswendig, welche
 * Tage KW 39 sind, und kaum jemand rechnet aus zwei Daten die Nummer. Am
 * Telefon sagt man die Nummer, auf dem Papier stehen die Tage.
 */
export function wochenwort(
  von: string,
  t: (k: string, w?: Record<string, string | number>) => string,
  bis?: string | null,
): string {
  const montag = montagVon(new Date(`${von}T12:00:00`));
  /* **Über mehrere Wochen** (W1, 24.09.2026): „KW 39–41 · 21.09.–11.10." —
     die Nummern der ersten und der letzten Woche, die Tage von Montag bis
     Sonntag. Eine Woche bleibt „KW 39 · 21.09.–27.09.". */
  const letzter = bis ? montagVon(new Date(`${bis}T12:00:00`)) : montag;
  if (letzter.getTime() > montag.getTime()) {
    return t("woche.bereich", {
      kwVon: kwVon(montag),
      kwBis: kwVon(letzter),
      von: kurzDatum(montag),
      bis: kurzDatum(sonntagZu(letzter)),
    });
  }
  return t("woche.wort", {
    kw: kwVon(montag),
    von: kurzDatum(montag),
    bis: kurzDatum(sonntagZu(montag)),
  });
}

/**
 * Der Wähler: ein Knopf, der ein Blatt von unten öffnet.
 *
 * Dieselbe Form wie der Zeitraumwähler am Telefon (`bausteine/Zeitraum.tsx`,
 * „Am Telefon ein Blatt von unten") — und dieselbe am Schreibtisch, weil die
 * Liste kurz ist und ein zweiter Mechanismus für dieselbe Frage einer zu
 * viel wäre.
 */
export function Wochenwahl({ wert, bis, aendern, id, leerWort, beschriftung }: {
  /** Der Montag der gewählten Woche als `JJJJ-MM-TT`, oder leer. */
  wert: string | null;
  /**
   * Der letzte Tag, wenn der Zeitraum über mehrere Wochen geht (W1, 0188) —
   * der Sonntag der letzten Woche. Fehlt er, ist es eine Woche.
   */
  bis?: string | null;
  /**
   * Gibt Montag der ersten und Sonntag der letzten Woche zurück — beide,
   * damit der Aufrufer nicht rechnet. Bei einer Woche ist es dieselbe.
   */
  aendern: (woche: { von: string; bis: string }) => void;
  id?: string;
  /** Was auf dem Knopf steht, solange nichts gewählt ist. */
  leerWort?: string;
  /**
   * Was auf dem Knopf steht, wenn schon ein **genauer** Termin feststeht —
   * seine Tage statt der Woche (Systemprüfung B-02). Wer eine Woche wählt,
   * macht daraus wieder eine grobe Planung.
   */
  beschriftung?: string;
}) {
  const t = useT();
  const [offen, setzeOffen] = useState(false);
  /* ── Mehrere Wochen (Systemprüfung W1, Entscheidung (2), 24.09.2026) ──
     Lisa: *„einen groben Zeitraum (‚in den nächsten zwei Wochen')."* Der
     Regelfall bleibt ein Tipp auf eine Woche (Regel 11). Wer „Über mehrere
     Wochen" einschaltet, tippt die erste und dann die letzte Woche; der
     Zeitraum dazwischen ist gemeint und steht im Brett. */
  const [mehrere, setzeMehrere] = useState(false);
  const [anfang, setzeAnfang] = useState<string | null>(null);
  const schliessen = () => { setzeOffen(false); setzeAnfang(null); setzeMehrere(false); };
  const waehlen = (w: { von: string; bis: string }) => {
    if (!mehrere) { aendern(w); schliessen(); return; }
    if (!anfang || w.von < anfang) { setzeAnfang(w.von); return; }
    aendern({ von: anfang, bis: w.bis });
    schliessen();
  };

  const wochen = useMemo(
    () => Array.from({ length: WOCHEN_VORAUS }, (_, n) => {
      const montag = montagPlus(n);
      return {
        n,
        von: alsTag(montag),
        bis: alsTag(sonntagZu(montag)),
        kw: kwVon(montag),
        spanne: `${kurzDatum(montag)}–${kurzDatum(sonntagZu(montag))}`,
      };
    }),
    [],
  );

  const gewaehlt = wert ? alsTag(montagVon(new Date(`${wert}T12:00:00`))) : null;
  /* Was als gewählt markiert steht: im Bereich die Wochen von `anfang` bis
     dahin, sonst der gespeicherte Zeitraum. */
  const letzteGespeichert = bis ? alsTag(montagVon(new Date(`${bis}T12:00:00`))) : gewaehlt;
  const istGewaehlt = (montag: string): boolean => (mehrere
    ? anfang === montag
    : Boolean(gewaehlt && letzteGespeichert && montag >= gewaehlt && montag <= letzteGespeichert));

  return (
    <>
      <button
        type="button"
        id={id}
        className={`wochenwahl${wert ? " gewaehlt" : ""}`}
        onClick={() => setzeOffen(true)}
      >
        {wert ? (beschriftung ?? wochenwort(wert, t, bis)) : (leerWort ?? t("woche.leer"))}
      </button>
      <Blattvonunten
        offen={offen}
        schliessen={schliessen}
        form="unten"
        rast="gross"
        titel={t(mehrere ? (anfang ? "woche.titelBis" : "woche.titelVon") : "woche.titel")}
      >
        <label className="wochen-mehrere">
          <input
            type="checkbox"
            checked={mehrere}
            onChange={(e) => { setzeMehrere(e.target.checked); setzeAnfang(null); }}
          />
          {t("woche.mehrere")}
        </label>
        <div className="hv-liste">
          {wochen.map((w) => (
            <button
              type="button"
              key={w.von}
              className={`hv-zeile${istGewaehlt(w.von) ? " gewaehlt" : ""}`}
              aria-pressed={istGewaehlt(w.von)}
              disabled={mehrere && anfang !== null && w.von < anfang}
              onClick={() => waehlen({ von: w.von, bis: w.bis })}
            >
              {/* **Die Nummer ist der Name, die Tage stehen darunter.** In
                  der Disposition wird über KW gesprochen; wer die Tage
                  braucht, liest die zweite Zeile, ohne sie zu suchen. */}
              <b>{t("woche.kw", { kw: w.kw })}</b>
              <span>
                {w.n === 0 ? `${t("woche.diese")} ${t("allgemein.trennpunkt")} ` : ""}
                {w.n === 1 ? `${t("woche.naechste")} ${t("allgemein.trennpunkt")} ` : ""}
                {w.spanne}
              </span>
            </button>
          ))}
        </div>
      </Blattvonunten>
    </>
  );
}
